import { useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { Dialog } from '../ui.js';
import type { AgreementRow } from './Agreements.js';

/**
 * Won deal → producer (owner 2026-10-27). One dialog for both ways in: dragging a deal to Won on the sales board (signing
 * date + producer) and "Hand over" on an agreement signed earlier (producer only). The handover makes the client when the
 * deal was with a new company and Klara summarises the meetings and notes for the producer, so it takes a few seconds.
 */

const TX = texts(
  {
    won: 'Mark as won',
    handover: 'Hand over to a producer',
    signedOn: 'Signed on',
    producer: 'Producer',
    later: 'Hand over later',
    newClient: (n: string) => `${n} becomes a new client with the contact from the quote.`,
    what: 'The producer gets a notification and an email with a summary of the meetings and notes so far, saved as a pinned note on the client. Then invite the client’s contacts to the portal from the client card.',
    eSign: 'Signed on paper? Upload the signed PDF on the agreement under Agreements.',
    working: 'Klara is summarising the meetings and notes…',
    back: 'Back',
    done: (n: string) => `Handed over to ${n}`,
    doneNoKlara: (n: string) => `Handed over to ${n} (without a summary: Klara could not answer)`
  },
  {
    won: 'Markera som vunnen',
    handover: 'Lämna över till producent',
    signedOn: 'Signerad den',
    producer: 'Producent',
    later: 'Lämna över senare',
    newClient: (n: string) => `${n} blir en ny kund med kontakten från offerten.`,
    what: 'Producenten får en notis och ett mejl med en sammanfattning av möten och anteckningar hittills, sparad som fäst anteckning på kunden. Bjud sedan in kundens kontakter till portalen från kundkortet.',
    eSign: 'Signerat på papper? Ladda upp den signerade PDF:en på avtalet under Avtal.',
    working: 'Klara sammanfattar möten och anteckningar…',
    back: 'Tillbaka',
    done: (n: string) => `Överlämnad till ${n}`,
    doneNoKlara: (n: string) => `Överlämnad till ${n} (utan sammanfattning: Klara svarade inte)`
  }
);

export interface HandoverResult {
  summarized: boolean;
  name: string;
}

/** `won`: the deal is still sent – sign it first. Without it, the agreement is already signed. */
export function HandoverDialog(p: {
  api: Api;
  a: AgreementRow;
  producers: { id: string; name: string }[];
  won?: boolean;
  /** the client's producer, else the first */
  defaultProducer?: string | null;
  onClose: () => void;
  onDone: (r: HandoverResult | null) => void | Promise<void>;
}) {
  const t = TX[useLang()];
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
  const [date, setDate] = useState(today);
  const [producer, setProducer] = useState(p.defaultProducer && p.producers.some((x) => x.id === p.defaultProducer) ? p.defaultProducer : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async (hand: boolean) => {
    setBusy(true);
    setErr('');
    try {
      let rev = p.a.rev;
      if (p.won) rev = (await p.api.post<{ agreement: AgreementRow }>(`/api/agreements/${p.a.id}/status`, { to: 'signed', rev, signedOn: date })).agreement.rev;
      let r: HandoverResult | null = null;
      if (hand) {
        const x = await p.api.post<{ summarized: boolean }>(`/api/agreements/${p.a.id}/handover`, { producerId: producer, rev });
        r = { summarized: x.summarized, name: p.producers.find((y) => y.id === producer)?.name ?? '' };
      }
      await p.onDone(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={p.won ? t.won : t.handover}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose} disabled={busy}>
            {t.back}
          </button>
          {p.won ? (
            <button type="button" class="btn" disabled={busy} onClick={() => void go(false)}>
              {t.later}
            </button>
          ) : null}
          <button type="button" class="btn btn-primary" disabled={busy || !producer} onClick={() => void go(true)}>
            {p.won ? t.won : t.handover}
          </button>
        </>
      }
    >
      <div class="stack">
        {p.won ? (
          <div class="field">
            <label class="label" for="ho-date">
              {t.signedOn}
            </label>
            <input class="input input--date" type="date" id="ho-date" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
          </div>
        ) : null}
        <div class="field">
          <label class="label" for="ho-producer">
            {t.producer}
          </label>
          <select class="input input--wide" id="ho-producer" value={producer} onChange={(e) => setProducer((e.target as HTMLSelectElement).value)}>
            <option value="">–</option>
            {p.producers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        {!p.a.client ? <p class="hint">{t.newClient(p.a.name)}</p> : null}
        <p class="hint">{t.what}</p>
        {p.won ? <p class="hint">{t.eSign}</p> : null}
        {busy ? (
          <p class="muted" role="status">
            {t.working}
          </p>
        ) : null}
        {err ? (
          <p class="field-error" role="alert">
            {err}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

export const handoverToast = (lang: 'en' | 'sv', r: HandoverResult) => (r.summarized ? TX[lang].done(r.name) : TX[lang].doneNoKlara(r.name));
